import { useCallback, useEffect, useState } from 'react';
import { AxiosError } from 'axios';

import { PublicationSetting } from 'data/generated/graphql';
import {
  SnippetyChartData,
  SnippetyResponseWithChart,
  SnippetyResponseWithImage,
  SnippetyResponseWithVideo,
  useSnippetyQuery,
  VideoDataForSummarian
} from 'hooks/useSnippetyQuery';
import { getDomain } from './altSummDomain';

import { ControlledAltSumm, DEFAULT_MEDIA_ALT_SUMM } from 'components/altsumm-modal/AltSummModal.utils';
import {
  CHART_ID_PATTERN,
  errorMessages,
  getChartData,
  getImageData,
  getVideoData,
  GUID_PATTERN,
  IMAGE_MANAGER_ID_PATTERN,
  MediaTypes
} from 'components/altsumm-modal/components/media-input/MediaInput.utils';

export { getDomain };

export const useMediaForm = (
  type: MediaTypes | '',
  onAdd: (data: SnippetyResponseWithImage | VideoDataForSummarian | SnippetyChartData | null) => void,
  setControlledAltSumm: React.Dispatch<React.SetStateAction<ControlledAltSumm>>,
  publicationSettings?: PublicationSetting
) => {
  const [isIdFormOpen, setIsIdFormOpen] = useState(false);
  const [id, setId] = useState('');
  const [error, setError] = useState('');
  const [idData, setIdData] = useState<SnippetyResponseWithImage | VideoDataForSummarian | SnippetyChartData | null>(
    null
  );

  const domain = getDomain(publicationSettings, type, id);

  const { data, error: idError, isLoading: isDataLoading } = useSnippetyQuery(domain);

  const typedImageData = type === 'image' && data ? (data as SnippetyResponseWithImage) : null;
  const typedVideoData = type === 'video' && data ? (data as SnippetyResponseWithVideo) : null;
  const typedChartData = type === 'chart' && data ? (data as SnippetyResponseWithChart) : null;

  useEffect(() => {
    setId('');
    setError('');
  }, [type]);

  const isValid = useCallback(
    (currentId: string) => {
      if (currentId.length === 0 || type === '') {
        return true;
      }

      if (type === 'image' && !IMAGE_MANAGER_ID_PATTERN.test(currentId)) {
        return false;
      }

      if (type === 'video' && !GUID_PATTERN.test(currentId)) {
        return false;
      }

      if (type === 'chart' && !CHART_ID_PATTERN.test(currentId)) {
        return false;
      }
      return true;
    },
    [type]
  );

  useEffect(() => {
    if (data?.error) {
      setError((data as { message: string; code: string; error: boolean }).message);
      return;
    }
    if (type === 'image' && typedImageData) {
      setIdData(getImageData(data as SnippetyResponseWithImage));
    }
    if (type === 'video' && typedVideoData) {
      setIdData(getVideoData(data as SnippetyResponseWithVideo));
    }
    if (type === 'chart' && typedChartData) {
      setIdData(getChartData(data as SnippetyResponseWithChart));
    }
  }, [data, setIdData, type, typedChartData, typedImageData, typedVideoData]);

  useEffect(() => {
    if (id.length === 0) {
      setError('');
    } else if (!domain && id.length > 0 && type !== '' && error !== errorMessages[type].invalidId && isIdFormOpen) {
      setError(errorMessages[type].invalidId);
    } else if (idError && type !== '' && idError !== error) {
      setError((idError as AxiosError<{ message: string }>).response?.data.message ?? errorMessages[type].serverError);
    }
  }, [idError, type, error, domain, id, isIdFormOpen]);

  const onDelete = () => {
    setId('');
    onAdd(null);
    setControlledAltSumm((prev) => ({
      ...prev,
      ...DEFAULT_MEDIA_ALT_SUMM
    }));
  };

  const onIdAdd = useCallback(() => {
    if (id.length === 0) {
      if (type in errorMessages) {
        setError(errorMessages[type as keyof typeof errorMessages].invalidInput);
      }
      return;
    }

    if (type === 'image' && !IMAGE_MANAGER_ID_PATTERN.test(id)) {
      setError(errorMessages.image.invalidId);
      return;
    }

    if (type === 'video' && !GUID_PATTERN.test(id)) {
      setError(errorMessages.video.invalidId);
      return;
    }

    if (type === 'chart' && !CHART_ID_PATTERN.test(id)) {
      setError(errorMessages.chart.invalidId);
      return;
    }

    onAdd(idData);
    setId('');
    setIsIdFormOpen(false);
  }, [id, type, idData, onAdd]);

  const onIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | null) => {
      if (e === null) {
        setId('');
      } else {
        const rawValue = e.target.value.trim();

        if (isValid(rawValue)) {
          setError('');
        }
        setId(rawValue);
      }
    },
    [isValid]
  );

  const shouldAdd = !!(!isDataLoading && !idError && id.length > 0);
  return {
    id,
    error,
    onDelete,
    onIdAdd,
    onIdChange,
    isIdFormOpen,
    shouldAdd,
    setIsIdFormOpen
  };
};
